// Material-UI版 従業員管理メインページ

import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button as MuiButton,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Pagination,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useEmployeeManagement } from '../../hooks/useEmployeeManagement';
import { CompanyEmployee, EmployeeStatus } from '../../types/employee';
import { EmployeeTableMUI } from './EmployeeTableMUI';
import { AddEmployeeModalMUI } from './AddEmployeeModalMUI';
import { EmployeeDetailModal } from './EmployeeDetailModal';
import { EmployeeEditModal } from './EmployeeEditModal';

interface EmployeeManagementPageProps {
  companyId: number;
}

export const EmployeeManagementPageMUI: React.FC<
  EmployeeManagementPageProps
> = ({ companyId }) => {
  const { user } = useAuth();
  const {
    employees,
    loading,
    filters,
    pagination,
    modals,
    selectedEmployee,
    updateFilters,
    updatePage,
    updateSort,
    addEmployee,
    updateEmployee,
    updateEmployeeStatus,
    openAddModal,
    closeAddModal,
    openEditModal,
    closeEditModal,
    openDetailModal,
    closeDetailModal,
  } = useEmployeeManagement(companyId);

  // 詳細表示から編集への遷移
  const handleEditFromDetail = (employee: CompanyEmployee) => {
    closeDetailModal();
    openEditModal(employee);
  };

  // Phase 2 Week 2: 権限チェック（企業管理者・HR・admin）
  if (!user || !['company_admin', 'hr', 'admin'].includes(user.userRole)) {
    return (
      <Container
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ maxWidth: 500, textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ mb: 2, color: '#111827' }}
            >
              アクセス権限がありません
            </Typography>
            <Typography variant="body1" sx={{ color: '#6B7280', mb: 3 }}>
              従業員管理機能は企業管理者または人事担当者のみ利用できます。
            </Typography>
            {/* Phase 2 Week 2: 権限不足時の詳細説明 */}
            <Alert severity="info" sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                💡 この機能について
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                従業員管理機能では、以下の操作が可能です：
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, textAlign: 'left' }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  従業員の追加・編集・削除（論理削除）
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  ロール変更（企業管理者・人事担当者・一般従業員）
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  ステータス変更（有効・無効）
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  従業員情報の閲覧
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>注意：</strong>自分のアカウント削除時の挙動はロールにより異なります
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 2 }}>
                この機能を利用するには、企業管理者または人事担当者に権限変更を依頼してください。
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const activeCount = employees.filter((emp) => emp.status === 'active').length;

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'flex-start', md: 'space-between' },
            alignItems: { xs: 'flex-start', md: 'flex-start' },
            gap: { xs: 2, md: 0 },
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              従業員管理
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              企業の従業員アカウントを管理します
            </Typography>
          </Box>
          <MuiButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddModal}
            size="small"
            sx={{
              mt: { xs: 0, md: 1 },
              alignSelf: { xs: 'flex-end', md: 'flex-start' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '6px 12px', sm: '8px 16px' },
              whiteSpace: 'nowrap',
            }}
          >
            従業員を追加
          </MuiButton>
        </Box>

        {/* 注意書き - ロール別表示 */}
        {user?.userRole === 'company_admin' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>⚠️ 重要：企業管理者の退会リスク</strong>
              <br />
              <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                あなた（企業管理者）が退会すると、全従業員（人事担当者含む）も同時に強制退会されます（連動削除）
              </span>
              <br />
              この連動削除は取り消せません。企業管理者アカウントは複数名作成するか、
              日常業務は人事担当者アカウントで行うことを強く推奨します。
              <br />
              <br />
              <strong>あなたのロール：企業管理者</strong>
              <br />
              • 退会すると全従業員も連動削除されます ⚠️
              <br />
              • 人事担当者の作成を推奨します
            </Typography>
          </Alert>
        )}
        {user?.userRole === 'hr' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>💡 人事担当者の退会について</strong>
              <br />
              あなた（人事担当者）が退会しても、本人のみが削除されます。
              他の従業員には影響ありません。
              <br />
              <br />
              <strong>あなたのロール：人事担当者</strong>
              <br />
              • 退会しても本人のみ削除（他の従業員に影響なし） ✅
              <br />
              • 企業管理者と同等の権限を持ちます
            </Typography>
          </Alert>
        )}

        {/* 検索・フィルター */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="名前を入力して検索..."
                  value={filters.search || ''}
                  onChange={(e) => updateFilters({ search: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="権限で絞り込み"
                  value={filters.role || ''}
                  onChange={(e) =>
                    updateFilters({
                      role: (e.target.value || undefined) as any,
                    })
                  }
                >
                  <MenuItem value="">すべての権限</MenuItem>
                  <MenuItem value="company_admin">企業管理者</MenuItem>
                  <MenuItem value="hr">人事担当者</MenuItem>
                  <MenuItem value="employee">一般従業員</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="ステータスで絞り込み"
                  value={filters.status || ''}
                  onChange={(e) =>
                    updateFilters({
                      status: (e.target.value || undefined) as EmployeeStatus,
                    })
                  }
                >
                  <MenuItem value="">すべてのステータス</MenuItem>
                  <MenuItem value="active">有効</MenuItem>
                  <MenuItem value="inactive">無効</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* メインコンテンツ */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <EmployeeTableMUI
            employees={employees}
            onSort={updateSort}
            onViewDetail={openDetailModal}
            onEdit={openEditModal}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
          />

          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(event, page) => updatePage(page)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* モーダル */}
      <AddEmployeeModalMUI
        isOpen={modals.addEmployee}
        onClose={closeAddModal}
        onSubmit={addEmployee}
      />


      {/* 従業員詳細モーダル */}
      <EmployeeDetailModal
        open={modals.employeeDetail}
        employee={selectedEmployee}
        onClose={closeDetailModal}
        onEdit={handleEditFromDetail}
      />

      {/* 従業員編集モーダル */}
      <EmployeeEditModal
        open={modals.editEmployee}
        employee={selectedEmployee}
        companyId={companyId}
        onClose={closeEditModal}
        onSuccess={updateEmployee}
      />
    </Container>
  );
};
